export type BrowserObservationRead =
  | "text"
  | "value"
  | "checked"
  | "selected"
  | "attribute"
  | "tag"
  | "visible"
  | "enabled";

export interface BrowserObservationField {
  key: string;
  read: BrowserObservationRead;
  attribute?: string;
}
export interface BrowserObservationQuery {
  id: string;
  selector: string;
  fields: BrowserObservationField[];
  limit: number;
}
export interface BrowserObservationPlan {
  version: 1;
  queries: BrowserObservationQuery[];
}
export type BrowserObservationRecord = Record<
  string,
  string | number | boolean | null
>;
export interface BrowserObservationSnapshot {
  version: 1;
  documentId: string;
  sequence: number;
  observedAt: number;
  context: {
    locationId: string;
    pathname: string;
    visibilityState: "visible" | "hidden";
  };
  results: Array<{
    id: string;
    status: "ok" | "unavailable" | "redacted";
    records: BrowserObservationRecord[];
    truncated: boolean;
  }>;
  truncated: boolean;
}
export type BrowserObservationCommand =
  | { action: "capabilities" }
  | { action: "read"; plan: BrowserObservationPlan }
  | {
      action: "subscribe";
      subscriptionId: string;
      plan: BrowserObservationPlan;
      intervalMs: number;
      ttlMs: number;
    }
  | { action: "poll"; subscriptionId: string }
  | { action: "unsubscribe"; subscriptionId: string };
export type BrowserObservationResult =
  | {
      action: "capabilities";
      version: 1;
      read: true;
      subscriptions: true;
      persistentStorage: false;
    }
  | { action: "read"; snapshot: BrowserObservationSnapshot }
  | {
      action: "subscribe";
      subscriptionId: string;
      expiresAt: number;
      snapshot: BrowserObservationSnapshot;
    }
  | {
      action: "poll";
      subscriptionId: string;
      expiresAt: number;
      snapshot: BrowserObservationSnapshot | null;
    }
  | { action: "unsubscribe"; subscriptionId: string };

const identifier = /^[A-Za-z_][A-Za-z0-9_-]{0,63}$/;
const uuid =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const sensitive =
  /(?:password|passwd|secret|token|api[_-]?key|credential|otp|one[_-]?time|2fa|mfa|verification[_-]?code|bearer|jwt|(?:^|[_-])auth(?:entication|orization)?(?:$|[_-]))/i;
const reads: readonly BrowserObservationRead[] = [
  "text",
  "value",
  "checked",
  "selected",
  "attribute",
  "tag",
  "visible",
  "enabled",
];
function object(
  value: unknown,
  keys: readonly string[],
  required: readonly string[] = keys,
): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return false;
  const own = Object.keys(value);
  return (
    own.every((key) => keys.includes(key)) &&
    required.every((key) => Object.hasOwn(value, key))
  );
}
function id(value: unknown): value is string {
  return (
    typeof value === "string" &&
    identifier.test(value) &&
    !["__proto__", "prototype", "constructor"].includes(value)
  );
}
function attribute(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= 64 &&
    /^(?:id|class|role|title|name|type|href|aria-[a-z0-9_-]+|data-[a-z0-9_-]+)$/.test(
      value,
    ) &&
    !sensitive.test(value)
  );
}
function uint(value: unknown, min: number, max: number): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= min &&
    value <= max
  );
}
function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
// Deliberately small CSS subset. Full CSS allows functions, escaping and selector lists.
export function isBrowserObservationSelector(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    !value ||
    value.length > 256 ||
    sensitive.test(value) ||
    /[\\,*:+~(){}\n\r\t]/.test(value)
  )
    return false;
  const compound =
    '(?:[A-Za-z][A-Za-z0-9-]*)?(?:(?:#[A-Za-z_][A-Za-z0-9_-]*)|(?:\\.[A-Za-z_][A-Za-z0-9_-]*)|(?:\\[(?:id|class|role|title|name|type|href|aria-[a-z0-9_-]+|data-[a-z0-9_-]+)(?:="[A-Za-z0-9 _./-]{0,64}")?\\]))*';
  const part = new RegExp(`^${compound}$`);
  if (value !== value.trim()) return false;
  const pieces: string[] = [];
  let current = "";
  let bracket = false;
  let quoted = false;
  let needCompound = false;
  for (let index = 0; index < value.length; index++) {
    const char = value[index]!;
    if (char === '"' && bracket) quoted = !quoted;
    if (char === "[" && !quoted) {
      if (bracket) return false;
      bracket = true;
    }
    if (char === "]" && !quoted) {
      if (!bracket) return false;
      bracket = false;
    }
    if (!bracket && !quoted && (char === " " || char === ">")) {
      if (current) {
        pieces.push(current);
        current = "";
        needCompound = false;
      }
      if (char === ">") {
        if (needCompound || !pieces.length) return false;
        needCompound = true;
      }
      continue;
    }
    current += char;
    if (needCompound) needCompound = false;
  }
  if (bracket || quoted || needCompound) return false;
  if (current) pieces.push(current);
  if (pieces.length < 1 || pieces.length > 4) return false;
  return pieces.every(
    (piece) =>
      piece.length > 0 &&
      part.test(piece) &&
      piece !== "" &&
      !/^\[?(?:data-|aria-)?(?:password|token|secret)/i.test(piece),
  );
}
function parseBrowserObservationPlanUnsafe(
  value: unknown,
): BrowserObservationPlan | null {
  if (
    !object(value, ["version", "queries"]) ||
    value.version !== 1 ||
    !Array.isArray(value.queries) ||
    value.queries.length > 16
  )
    return null;
  const seen = new Set<string>();
  const queries: BrowserObservationQuery[] = [];
  for (const raw of value.queries) {
    if (
      !object(raw, ["id", "selector", "fields", "limit"]) ||
      !id(raw.id) ||
      seen.has(raw.id) ||
      !isBrowserObservationSelector(raw.selector) ||
      !Array.isArray(raw.fields) ||
      raw.fields.length < 1 ||
      raw.fields.length > 8 ||
      !uint(raw.limit, 1, 25)
    )
      return null;
    seen.add(raw.id);
    const fields: BrowserObservationField[] = [];
    const keys = new Set<string>();
    for (const field of raw.fields) {
      if (
        !object(field, ["key", "read", "attribute"], ["key", "read"]) ||
        !id(field.key) ||
        keys.has(field.key) ||
        !reads.includes(field.read as BrowserObservationRead)
      )
        return null;
      if (
        field.read === "attribute"
          ? !attribute(field.attribute)
          : Object.hasOwn(field, "attribute")
      )
        return null;
      keys.add(field.key);
      fields.push(
        field.read === "attribute"
          ? {
              key: field.key,
              read: field.read,
              attribute: field.attribute as string,
            }
          : { key: field.key, read: field.read as BrowserObservationRead },
      );
    }
    queries.push({
      id: raw.id,
      selector: raw.selector,
      fields,
      limit: raw.limit,
    });
  }
  return { version: 1, queries };
}
function parseBrowserObservationCommandUnsafe(
  value: unknown,
): BrowserObservationCommand | null {
  if (
    !object(
      value,
      ["action", "plan", "subscriptionId", "intervalMs", "ttlMs"],
      ["action"],
    )
  )
    return null;
  switch (value.action) {
    case "capabilities":
      return Object.keys(value).length === 1
        ? { action: "capabilities" }
        : null;
    case "read": {
      if (Object.keys(value).length !== 2) return null;
      const plan = parseBrowserObservationPlan(value.plan);
      return plan ? { action: "read", plan } : null;
    }
    case "subscribe": {
      if (
        Object.keys(value).length !== 5 ||
        typeof value.subscriptionId !== "string" ||
        !uuid.test(value.subscriptionId) ||
        !uint(value.intervalMs, 1000, 60000) ||
        !uint(value.ttlMs, 5000, 300000)
      )
        return null;
      const plan = parseBrowserObservationPlan(value.plan);
      return plan
        ? {
            action: "subscribe",
            subscriptionId: value.subscriptionId,
            plan,
            intervalMs: value.intervalMs,
            ttlMs: value.ttlMs,
          }
        : null;
    }
    case "poll":
    case "unsubscribe":
      return Object.keys(value).length === 2 &&
        typeof value.subscriptionId === "string" &&
        uuid.test(value.subscriptionId)
        ? { action: value.action, subscriptionId: value.subscriptionId }
        : null;
    default:
      return null;
  }
}
function parseBrowserObservationSnapshotUnsafe(
  value: unknown,
): BrowserObservationSnapshot | null {
  let bytes: number;
  try {
    bytes = new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return null;
  }
  if (
    !object(value, [
      "version",
      "documentId",
      "sequence",
      "observedAt",
      "context",
      "results",
      "truncated",
    ]) ||
    bytes > 32768 ||
    value.version !== 1 ||
    typeof value.documentId !== "string" ||
    !uuid.test(value.documentId) ||
    !uint(value.sequence, 0, Number.MAX_SAFE_INTEGER) ||
    !finite(value.observedAt) ||
    typeof value.truncated !== "boolean" ||
    !object(value.context, ["locationId", "pathname", "visibilityState"]) ||
    typeof value.context.locationId !== "string" ||
    !/^[A-Za-z0-9_-]{1,128}$/.test(value.context.locationId) ||
    typeof value.context.pathname !== "string" ||
    value.context.pathname.length > 512 ||
    /[?#]/.test(value.context.pathname) ||
    /(?:^|\/)(?:settings?|integrations?|admin|api-keys?|credentials?)(?:\/|$)/i.test(
      value.context.pathname,
    ) ||
    !new RegExp(`^/v2/location/${value.context.locationId}(?:/|$)`).test(
      value.context.pathname,
    ) ||
    !["visible", "hidden"].includes(value.context.visibilityState as string) ||
    !Array.isArray(value.results) ||
    value.results.length > 16
  )
    return null;
  const seen = new Set<string>();
  const results: BrowserObservationSnapshot["results"] = [];
  for (const raw of value.results) {
    if (
      !object(raw, ["id", "status", "records", "truncated"]) ||
      !id(raw.id) ||
      seen.has(raw.id) ||
      !["ok", "unavailable", "redacted"].includes(raw.status as string) ||
      typeof raw.truncated !== "boolean" ||
      !Array.isArray(raw.records) ||
      raw.records.length > 25 ||
      (raw.status !== "ok" && raw.records.length > 0)
    )
      return null;
    seen.add(raw.id);
    const records: BrowserObservationRecord[] = [];
    for (const record of raw.records) {
      if (
        record === null ||
        typeof record !== "object" ||
        Array.isArray(record) ||
        Object.keys(record).length > 8
      )
        return null;
      const copy: BrowserObservationRecord = Object.create(
        null,
      ) as BrowserObservationRecord;
      for (const [key, cell] of Object.entries(record)) {
        if (
          !id(key) ||
          !(
            cell === null ||
            typeof cell === "boolean" ||
            (typeof cell === "number" && Number.isFinite(cell)) ||
            (typeof cell === "string" && cell.length <= 2048)
          )
        )
          return null;
        copy[key] = cell;
      }
      records.push(copy);
    }
    results.push({
      id: raw.id,
      status: raw.status as "ok" | "unavailable" | "redacted",
      records,
      truncated: raw.truncated,
    });
  }
  return {
    version: 1,
    documentId: value.documentId,
    sequence: value.sequence,
    observedAt: value.observedAt,
    context: {
      locationId: value.context.locationId,
      pathname: value.context.pathname,
      visibilityState: value.context.visibilityState as "visible" | "hidden",
    },
    results,
    truncated: value.truncated,
  };
}
export function browserObservationSnapshotMatchesPlan(
  snapshot: BrowserObservationSnapshot,
  plan: BrowserObservationPlan,
): boolean {
  return (
    snapshot.results.length === plan.queries.length &&
    snapshot.results.every(
      (result, index) =>
        result.id === plan.queries[index]?.id &&
        result.records.length <= plan.queries[index]!.limit &&
        result.records.every(
          (record) =>
            Object.keys(record).length === plan.queries[index]!.fields.length &&
            plan.queries[index]!.fields.every((field) => {
              const cell = record[field.key];
              return (
                Object.hasOwn(record, field.key) &&
                (cell === null ||
                  (["checked", "selected", "visible", "enabled"].includes(
                    field.read,
                  )
                    ? typeof cell === "boolean"
                    : typeof cell === "string"))
              );
            }),
        ),
    )
  );
}
function parseBrowserObservationResultUnsafe(
  value: unknown,
): BrowserObservationResult | null {
  if (
    !object(
      value,
      [
        "action",
        "version",
        "read",
        "subscriptions",
        "persistentStorage",
        "snapshot",
        "subscriptionId",
        "expiresAt",
      ],
      ["action"],
    )
  )
    return null;
  if (value.action === "capabilities")
    return object(value, [
      "action",
      "version",
      "read",
      "subscriptions",
      "persistentStorage",
    ]) &&
      value.version === 1 &&
      value.read === true &&
      value.subscriptions === true &&
      value.persistentStorage === false
      ? {
          action: "capabilities",
          version: 1,
          read: true,
          subscriptions: true,
          persistentStorage: false,
        }
      : null;
  if (value.action === "read") {
    const snapshot = parseBrowserObservationSnapshot(value.snapshot);
    return Object.keys(value).length === 2 && snapshot
      ? { action: "read", snapshot }
      : null;
  }
  if (
    typeof value.subscriptionId !== "string" ||
    !uuid.test(value.subscriptionId)
  )
    return null;
  if (value.action === "unsubscribe")
    return Object.keys(value).length === 2
      ? { action: "unsubscribe", subscriptionId: value.subscriptionId }
      : null;
  if (
    (value.action === "subscribe" || value.action === "poll") &&
    Object.keys(value).length === 4 &&
    finite(value.expiresAt)
  ) {
    const snapshot =
      value.snapshot === null && value.action === "poll"
        ? null
        : parseBrowserObservationSnapshot(value.snapshot);
    return snapshot === null &&
      (value.action !== "poll" || value.snapshot !== null)
      ? null
      : ({
          action: value.action,
          subscriptionId: value.subscriptionId,
          expiresAt: value.expiresAt,
          snapshot,
        } as BrowserObservationResult);
  }
  return null;
}

// PostMessage values are normally JSON, but local callers may pass exotic objects.
// All public parse boundaries remain total and never include input in errors.
export function parseBrowserObservationPlan(
  value: unknown,
): BrowserObservationPlan | null {
  try {
    return parseBrowserObservationPlanUnsafe(value);
  } catch {
    return null;
  }
}
export function parseBrowserObservationCommand(
  value: unknown,
): BrowserObservationCommand | null {
  try {
    return parseBrowserObservationCommandUnsafe(value);
  } catch {
    return null;
  }
}
export function parseBrowserObservationSnapshot(
  value: unknown,
): BrowserObservationSnapshot | null {
  try {
    return parseBrowserObservationSnapshotUnsafe(value);
  } catch {
    return null;
  }
}
export function parseBrowserObservationResult(
  value: unknown,
): BrowserObservationResult | null {
  try {
    return parseBrowserObservationResultUnsafe(value);
  } catch {
    return null;
  }
}
